<template>
  <div class="wt-container">
    <div v-if="branding == 'wfn'" class="block">
      <button @click="toggleMoreInfo()" title="More info about the Water Footprint Assessment Tool" class="btn btn-lg btn-light"><i class="fa-solid fa-circle-info"></i></button>
      <h1 class="mt-5 text-center">Water Footprint Assessment&nbsp;Tool</h1>
      <p class="my-5 mx-auto lead text-center" style="max-width: 1120px">
        Welcome! This tool will help you calculate and map water footprint, compare it to water availability and water pollution levels, and identify strategic actions to improve the sustainability, efficiency and equitability of freshwater use. Start an assessment by selecting your scope from the options below.
      </p>
      <template v-if="moreInfo">
        <p>
          The Water Footprint Assessment Tool is co-developed by <a href="https://www.waterfootprintimplementation.com/">Water Footprint Implementation</a> and the <a href="https://www.waterfootprint.org/">Water Footprint Network</a>.
          Bringing together the internationally recognized <a href="https://www.waterfootprint.org/water-footprint-2/what-is-water-footprint-assessment/">Global Water Footprint Standard</a> and the most comprehensive, global water footprint database – <a href="https://www.waterfootprint.org/publications/">WaterPub</a>, the Water Footprint Assessment Tool allows you to complete assessments from a geographic and from a production perspective, and provides results based on sound methods and best available data.
        </p>
        <h4>Privacy Policy</h4>
        <p>
          To perform a commodity or business assessment, you need an account. By registering and using your account, you acknowledge and agree to the following:
        </p>
        <ol type="1">
          <li>Data access: The data you input into the tool may be accessed by our development team for internal monitoring, evaluation, and improvement of the tool. Any data used will be treated in accordance with our privacy policy.</li>
          <li>Communication consent: You grant Water Footprint Implementation permission to contact you via the registration details you provided, for purposes related to the use of the tool, updates, and feedback.</li>
        </ol>
        <h4>Contact</h4>
        <p>
          For practical applications: <a href="mailto:info@waterfootprintimplementation.com">info@waterfootprintimplementation.com</a><br/>
          For community and academic inquiries: <a href="mailto:info@waterfootprint.org">info@waterfootprint.org</a>
        </p>
        <h4>Copyright (2020)</h4>
        <p>Water Footprint Implementation, Water Footprint Network, Arjen Hoekstra & Mesfin Mekonnen</p>
        <h4>Development Team</h4>
        <p class="mb-5">Michiel van Heek, Istvan Baro, Csaba Bigyi, Ryan Prater</p>
      </template>
    </div>
    <div v-else class="block">
      <button @click="toggleMoreInfo()" title="More info about the global freshwater quantity model" class="btn btn-lg btn-light"><i class="fa-solid fa-circle-info"></i></button>
      <h1 class="mt-5 text-center">SBTN Step 3: Freshwater - Global Freshwater Quantity Model</h1>
      <p class="my-5 mx-auto lead text-center" style="max-width: 1220px">
        This is an adapted version of the Water Footprint Assessment Tool, made for the <a href="https://sciencebasedtargetsnetwork.org/">Science-Based Targets Network</a>. It can be used to assist companies in completing <b>Step 1: Assess</b> and <b>Step 2: Interpret & Prioritize</b>, using the "Accounting" function to gather data on water pressures, and the <b>Step 3: Freshwater</b> method for target setting (v1), using the "Sustainability" function to establish a baseline and target using the globally-developed modelling approach. The “Response Formulation” function is under consideration for development to support the implementation of <b>Step 4: Act</b>. Please start an assessment by selecting one of the options below.
      </p>
      <template v-if="moreInfo">
        <p>
          The model is built on <a href="https://agupubs.onlinelibrary.wiley.com/doi/full/10.1029/2019EF001363">Hogeboom et al. (2020)</a> and
          the <a href="/">Water Footprint Assessment Tool</a>, that helps you calculate and map water footprint, assess its sustainability, and identify strategic actions to improve the sustainability, efficiency and equitability of water use.
          Bringing together the internationally recognized <a href="https://www.waterfootprint.org/water-footprint-2/what-is-water-footprint-assessment/">Global Water Footprint Standard</a> and the most comprehensive, global water footprint database – <a href="https://www.waterfootprint.org/publications/">WaterPub</a>, the Water Footprint Assessment Tool allows you to complete water assessments and provides results based on sound methods and the best available data.
          <a href="https://www.waterfootprintimplementation.com/">Water Footprint Implementation</a> developed the Water Footprint Assessment Tool in collaboration with the <a href="https://www.waterfootprint.org/">Water Footprint Network</a>.
        </p>
        <p class="mb-5">
          For more details and to download current and previous versions of the global data layers underlying this tool, please refer to DOI:<br/><a href="https://data.4tu.nl/datasets/089afa18-bbd5-493e-8a6c-39e1ca7b2a59">089afa18-bbd5-493e-8a6c-39e1ca7b2a59</a>
        </p>
      </template>
    </div>
<!--    <div v-if="branding == 'wfn' && newVersion" class="alert alert-warning alert-dismissible" style="margin-top: -1rem">
      <template v-if="!learnMore">
        <b>PM</b> PM<br/>
        <a href="#" @click.prevent="toggleLearnMore(); $event.preventDefault()">Learn more</a>
      </template>
      <span v-else :innerHTML="learnMore"></span>
      <button type="button" @click="toggleNewVersion()" class="btn-close" aria-label="Close"></button>
    </div>
    <div v-if="branding == 'sbtn' && newVersion" class="alert alert-warning alert-dismissible" style="margin-top: -1rem">
      <b>NEW DATA</b> The blue water reduction targets in this tool have been updated per 1 October 2025. As of this date, your business assessments may calculate different results compared to before.<br/>
      <button type="button" @click="toggleNewVersion()" class="btn-close" aria-label="Close"></button>
    </div>-->
    <div v-if="branding == 'wfn'" class="box-ct bg-light my-2">
        <div class="h-card" @click="showCountryDialog()" title="Assess the water footprint of a country">
          <div class="box" :class="{active: assessmentIsActive('countries')}">
            <img src="../assets/img/icons/country.png" class="h-card--icon" alt />
            <span class="box__title">a Country</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="showRiverBasinDialog()" title="Assess the water footprint of a river basin">
          <div class="box" :class="{active: assessmentIsActive('basins')}">
            <img src="../assets/img/icons/river-basin.png" class="h-card--icon" alt />
            <span class="box__title">a River Basin</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="worldNextStep()" title="Assess the water footprint of the world">
          <div class="box" :class="{active: assessmentIsActive('world')}">
            <img src="../assets/img/icons/world.png" class="h-card--icon" alt />
            <span class="box__title">the World</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="hbasinsNextStep()" title="Assess the water footprint of the world (Hydrosheds)">
          <div class="box" :class="{active: assessmentIsActive('hbasins')}">
            <img src="../assets/img/icons/world.png" class="h-card--icon" alt />
            <span class="box__title">the World (HS)</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="showCropDialog()" title="Assess the water footprint of a raw material">
          <div class="box" :class="{active: assessmentIsActive('crops')}">
            <img src="../assets/img/icons/raw-product.png" class="h-card--icon" alt />
            <span class="box__title">a Raw Product</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
          </div>
        </div>
        <div class="h-card" @click="showProductionDialog('commodity')" title="Assess the water footprint of my commodity">
          <div class="box" :class="{active: assessmentIsActive('commodity')}">
            <img src="../assets/img/icons/product.png" class="h-card--icon" alt />
            <span class="box__title">my Commodity</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
            <div v-if="numCommodityAssessments" class="label">
              {{ numCommodityAssessments }} assessments
            </div>
          </div>
        </div>
        <div class="h-card" @click="showProductionDialog('business')" title="Assess the water footprint of my business">
          <div class="box" :class="{active: assessmentIsActive('business')}">
            <img src="../assets/img/icons/business.png" class="h-card--icon" alt />
            <span class="box__title">my Business</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
            <div v-if="numBusinessAssessments" class="label">
              {{ numBusinessAssessments }} assessments
            </div>
          </div>
        </div>
    </div>
    <div v-else-if="branding == 'sbtn'" class="box-ct bg-light my-2">
        <div class="h-card noscale" @click="showProductionDialog('business')" title="Assess the water quantity targets of my business">
          <div class="box" :class="{active: assessmentIsActive('business')}">
            <img src="../assets/img/icons/business.png" class="h-card--icon" alt />
            <span class="box__title">my Business</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
            <div class="label">
              Upload & download CSV
            </div>
          </div>
        </div>
        <div class="h-card noscale" @click="hbasinsNextStep()">
          <div class="box" :class="{active: assessmentIsActive('hbasins')}" title="Assess the water quantity targets of the world">
            <img src="../assets/img/icons/world.png" class="h-card--icon" alt />
            <span class="box__title">the World</span>
            <div class="mask__ct">
              <div class="mask mask--1"></div>
              <div class="mask mask--2"></div>
              <div class="mask mask--3"></div>
            </div>
            <div class="label">
              Free browsing without CSV
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { assessment, assessmentTypes } from "@/services/assessment-model";
import { userModel } from "@/services/user-model";

export default {
  name: "Home",
  data() {
    return {
      branding: assessment.branding,
      moreInfo: false,
      learnMore: null,
      newVersion: true
    }
  },
  computed: {
    numCommodityAssessments() {
      return userModel.user?.scenarios.filter(s => s.type % 10 == 1).length;
    },
    numBusinessAssessments() {
      return userModel.user?.scenarios.filter(s => s.type % 100 >= 10).length;
    }
  },
  methods: {
    assessmentIsActive(assessmentTypeName) {
      return assessment.isActive() && (assessment.assessmentType.name == assessmentTypeName);
    },
    toggleMoreInfo() {
      this.moreInfo = !this.moreInfo;
    },
    toggleLearnMore() {
      this.learnMore = "PM";
    },
    toggleNewVersion() {
      this.newVersion = false;
    },
    showCountryDialog: function() {
      assessment.setAssessmentType(assessmentTypes.countries);
    },
    showRiverBasinDialog: function() {
      assessment.setAssessmentType(assessmentTypes.basins);
    },
    showCropDialog: function() {
        assessment.setAssessmentType(assessmentTypes.crops);
    },
    showProductionDialog(assessmentType) {
      assessment.setAssessmentType(assessmentType);
    },
    worldNextStep: function() {
      assessment.setAssessmentType(assessmentTypes.world);
      assessment.nextStep();
    },
    hbasinsNextStep: function() {
      assessment.setAssessmentType("hbasins");
      assessment.nextStep();
    }
  }
};
</script>

<style lang="scss" scoped>
.block {
  position: relative;
  padding: 0 10%;
  .btn-lg {
    position: absolute;
    right: 0;
    top: 52px;
  }
  h4 {
    margin: 0 0 15px 0;
    color: #009BD1;
    font-size: 1em;
  }
  a:hover {
    color: #41b883;
  }
}
.alert-dismissible {
  margin-left: 10%;
  margin-right: 10%;
}
.box-ct {
  display: flex;
  position: relative;
  padding: 65px 10%;
  flex-wrap: wrap;
  justify-content: center;
  gap: 65px 8%;

  @media (max-width: 1400px) {
    gap: 65px 11%;
  }
  @media (max-width: 992px) {
    gap: 70px 12%;
  }
}

.h-card {
  width: 19%;
  border-radius: 25px;
  background-color: #fff;
  cursor: pointer;

  @media (max-width: 1400px) {
    width: 26%;
  }
  @media (max-width: 992px) {
    width: 44%;
  }
  @media (max-width: 576px) {
    width: 100%;
  }

  &.noscale {
    width: 280px;
  }

  &--icon {
    position: absolute;
    left: 50%;
    top: 40%;
    transform: translateX(-50%) translateY(-50%) scale(0.5, 0.5);

    @media (max-width: 1400px) {
      transform: translateX(-50%) translateY(-50%) scale(0.6, 0.6);
    }
    @media (max-width: 1200px) {
      transform: translateX(-50%) translateY(-50%) scale(0.5, 0.5);
    }
    @media (max-width: 992px) {
      transform: translateX(-50%) translateY(-50%) scale(0.7, 0.7);
    }
    @media (max-width: 768px) {
      transform: translateX(-50%) translateY(-50%) scale(0.5, 0.5);
    }
    @media (max-width: 576px) {
      transform: translateX(-50%) translateY(-50%) scale(0.8, 0.8);
    }
  }

  &.noscale &--icon {
      transform: translateX(-50%) translateY(-50%) scale(0.6, 0.6);
  }

  .box {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 25px;
    padding-top: 80%;
    overflow: hidden;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 1px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

    @media (max-width: 1400px) {
      font-size: 20px;
      font-weight: 700;
    }
    @media (max-width: 1200px) {
      font-size: 18px;
      font-weight: 800;
    }
    @media (max-width: 992px) {
      font-size: 22px;
      font-weight: 700;
    }
    @media (max-width: 768px) {
      font-size: 18px;
      font-weight: 800;
    }
    @media (max-width: 576px) {
      font-size: 24px;
      font-weight: 700;
    }

    &__title {
      position: absolute;
      bottom: 5%;
      z-index: 4;
      width: 100%;
      color: white;
      text-align: center;
      text-transform: uppercase;
    }

    &:hover {
      box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.24);
    }
    &.active {
      border: 3px solid var(--bs-primary);
    }

    .label {
      position: absolute;
      left: 0;
      top: 0;
      border-top-left-radius: 20px;
      border-bottom-right-radius: 20px;
      padding: 8px 18px;
      font-size: 16px;
      font-weight: 400;
      font-style: italic;
      color: #fff;
      background-color: var(--bs-orange);
    }
  }

  .mask {
    position: absolute;
    left: 0;
    width: 800%;
    height: 100%;
    background-repeat: repeat no-repeat;
    background-position: left bottom;
    background-size: 100% 60%;
    transform-origin: left bottom;

    &__ct {
      position: absolute;
      width: 100%;
      overflow: hidden;
      height: 100%;
      bottom: 0;
    }

    &--1 {
      background-image: url("../assets/img/shape1_new.png");
      z-index: 3;
    }
    &--2 {
      background-image: url("../assets/img/shape2_new.png");
      z-index: 2;
    }
    &--3 {
      background-image: url("../assets/img/shape3_new.png");
      z-index: 1;
    }
  }
  &:hover {
    .mask--1 {
      animation: waves 10s linear infinite;
    }
    .mask--2 {
      animation: waves 20s linear infinite;
    }
    .mask--3 {
      animation: waves 15s linear infinite;
    }
  }
}

@keyframes waves {
  100% {
    transform: translateX(-50%) translateZ(0) scaleY(1);
    left: 0;
  }
  50% {
    transform: translateX(-25%) translateZ(0) scaleY(0.85);
  }
  0% {
    transform: translateX(0) translateZ(0) scaleY(1);
  }
}
</style>
